'use client';
import React, { ReactNode, useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import Image from 'next/image';
import ModalForm from '../Modal/Modal';
import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical';
import CustomRichText from '../CustomRichText';

export interface ProposalListBlockProps {
  id?: string;
  title: ReactNode | SerializedEditorState;
  color?: 'violet' | 'green' | 'blue';
  teachers?: {
    title: ReactNode | SerializedEditorState;
    subtitle: ReactNode | SerializedEditorState;
    srcImage: string | { url: string; alt: string };
  }[];
  buttonText?: string;
  btnTag?: string;
}

export default function TeacherBlockV2({
  title,
  id,
  color = 'green',
  teachers = [],
  buttonText,
  btnTag,
}: ProposalListBlockProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const openForm = () => setIsFormOpen(true);
  const closeForm = () => setIsFormOpen(false);

  const handlePrevClick = () => {
    setCurrentIndex(prevIndex => (prevIndex === 0 ? teachers.length - 1 : prevIndex - 1));
  };

  const handleNextClick = () => {
    setCurrentIndex(prevIndex => (prevIndex === teachers.length - 1 ? 0 : prevIndex + 1));
  };

  const currentInfo = teachers[currentIndex];

  const backgroundColorClass =
    {
      green: 'custom-gradient-green-btn-h',
      blue: 'custom-gradient-blue-btn-h',
      violet: 'custom-gradient-violet-btn-h',
    }[color] || 'custom-gradient-green-btn-h';

  const backgroundBtnColorClass =
    {
      green: 'custom-gradient-green-btn',
      blue: 'custom-gradient-blue-btn',
      violet: 'custom-gradient-violet-btn',
    }[color] || 'custom-gradient-green-btn';

  return (
    <>
      <div
        id={id}
        data-meta="TeacherBlockV2"
        className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 my-24"
      >
        <div className="px-4 py-5 sm:px-6 flex justify-between">
          <div className="text-4xl font-bold text-white">
            <CustomRichText data={title} tag="div" className="text-white" />
          </div>
          <div className="flex gap-x-2">
            <button
              onClick={handlePrevClick}
              className={`p-2 border border-gray-400 text-gray-400 rounded-full hover:border-white hover:text-white ${backgroundColorClass}`}
            >
              <ArrowLeftIcon className="h-6 w-6" />
            </button>
            <button
              onClick={handleNextClick}
              className={`p-2 border border-gray-400 text-gray-400 rounded-full hover:border-white hover:text-white ${backgroundColorClass}`}
            >
              <ArrowRightIcon className="h-6 w-6" />
            </button>
          </div>
        </div>
        <div className="bg-[#F5F5F7] max-w-max rounded-3xl flex flex-col justify-between md:grid grid-cols-2 md:gap-4 lg:gap-8 max-sm:overflow-hidden">
          <div className="ml-4 my-5 sm:ml-6 sm:my-6 flex flex-col justify-between shrink-1">
            <div>
              <div className="text-xl font-bold mb-6">
                <CustomRichText data={currentInfo.title} tag="div" />
              </div>
              <div className="text-sm lg:text-lg">
                <CustomRichText data={currentInfo.subtitle} tag="div" />
              </div>
            </div>
            <button
              onClick={openForm}
              className={`${backgroundBtnColorClass} hidden lg:block uppercase mt-4 max-w-min text-white rounded-full px-8 py-2.5 text-center text-sm font-fixel font-bold shadow-sm focus-visible:outline`}
            >
              {buttonText}
            </button>
          </div>
          <div className="rounded-3xl md:rounded-none">
            <div className="translate-y-5 sm:translate-y-7 md:translate-x-3 md:translate-y-0">
              <Image
                alt={''}
                src={
                  typeof currentInfo?.srcImage === 'string'
                    ? currentInfo?.srcImage
                    : currentInfo.srcImage.url || '/placeholder.jpg'
                }
                sizes="100vw"
                width={750}
                height={500}
                style={{
                  width: '100%',
                  height: 'auto',
                }}
                className="w-full rounded-3xl object-contain"
              />
            </div>
          </div>
        </div>
        <div className="mt-8">
          <button
            onClick={openForm}
            className={`${backgroundBtnColorClass} md:hidden uppercase mt-4 w-full text-white rounded-full px-8 py-2.5 text-center text-sm font-fixel font-bold shadow-sm focus-visible:outline`}
          >
            {buttonText}
          </button>
        </div>
      </div>
      {isFormOpen && (
        <ModalForm closeForm={closeForm} btnMeta={{ tag: btnTag ?? '' }} color={color} />
      )}
    </>
  );
}
